@if (isFixed() && (isOpen() || hasOpened())) {
  <div
    class="album-drawer-progressive-blur fixed top-16 inset-x-0 bottom-0 z-90 transition-opacity duration-300 ease-in-out cursor-pointer"
    [class.is-active]="isOpen()"
    [class.opacity-100]="isOpen()"
    [class.opacity-0]="!isOpen()"
    [class.pointer-events-none]="!isOpen()"
    (click)="closeSidebar()"
    aria-hidden="true"
  >
    <div class="blur-slice blur-slice-1"></div>
    <div class="blur-slice blur-slice-2"></div>
    <div class="blur-slice blur-slice-3"></div>
    <div class="blur-slice blur-slice-4"></div>
    <div class="blur-slice blur-slice-5"></div>
    <div class="blur-slice blur-slice-6"></div>
    <div class="blur-tint"></div>
  </div>
}

<aside
  class="flex flex-col bg-side-drawer-bg"
  [ngClass]="asideNgClass()"
>
  @if (selectedAlbumId()) {
    <div
      class="relative flex h-full min-h-0 flex-1 flex-col bg-side-drawer-bg text-text-primary"
    >
      <!-- Top Bar: Status / Role + Close Button (12px padding) -->
      <div class="flex items-center justify-between px-3 pt-3 pb-1 shrink-0">
        <span class="text-xs font-medium text-text-dimmed">Album / Release</span>
        <button
          type="button"
          (click)="closeSidebar()"
          class="rounded-full p-1.5 text-text-dimmed hover:text-text-primary hover:bg-white/10 transition-colors cursor-pointer"
          aria-label="Close"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <div id="album-profile-preview-drawer-scroll" class="custom-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-clip px-3 py-2">
        <div class="flex flex-col gap-4">
          <!-- Album Header info -->
          @if (loading()) {
            <div class="flex items-center gap-3.5 animate-pulse">
              <div class="h-[110px] w-[110px] shrink-0 rounded-2xl bg-card-bg-hover"></div>
              <div class="flex min-w-0 flex-1 flex-col gap-2">
                <div class="h-6 w-3/4 bg-card-bg-hover rounded"></div>
                <div class="h-4 w-1/2 bg-card-bg-hover rounded"></div>
                <div class="h-5 w-16 bg-card-bg-hover rounded-full"></div>
              </div>
            </div>
          } @else if (albumDetails(); as album) {
            <div class="flex items-center gap-3.5">
              <img
                [src]="album.imageUrl || albumImageFallback(album.title)"
                class="h-[110px] w-[110px] shrink-0 rounded-2xl border border-white/10 bg-zinc-800 object-cover shadow-lg"
                alt=""
              />
              <div class="flex min-w-0 flex-1 flex-col gap-1">
                <span class="text-[10px] font-bold uppercase tracking-wider text-primary">{{ getFormattedReleaseType(album) }}</span>
                <h3
                  class="font-bold leading-tight tracking-tight text-text-primary text-xl truncate"
                  [title]="album.title"
                >
                  {{ album.title }}
                </h3>
                <p class="text-xs text-text-half-dimmed font-medium truncate">
                  by {{ getArtistsList(album.artists) }}
                </p>
              </div>
            </div>

            <!-- Stats grid -->
            <div class="grid grid-cols-3 gap-2">
              <div class="rounded-md bg-card-bg-default p-2.5 text-center">
                <p class="text-[9px] font-bold uppercase tracking-wider text-text-dimmed">Tracks</p>
                <p class="text-base font-bold text-text-primary mt-0.5">{{ album.songs?.length || 0 }}</p>
              </div>
              <div class="rounded-md bg-card-bg-default p-2.5 text-center">
                <p class="text-[9px] font-bold uppercase tracking-wider text-text-dimmed">Streams</p>
                <p class="text-base font-bold text-emerald-400 mt-0.5">{{ formatStreams(album.streams) }}</p>
              </div>
              <div class="rounded-md bg-card-bg-default p-2.5 text-center">
                <p class="text-[9px] font-bold uppercase tracking-wider text-text-dimmed">Year</p>
                <p class="text-base font-bold text-primary mt-0.5">{{ album.releaseDate ? album.releaseDate.substring(0, 4) : '-' }}</p>
              </div>
            </div>

            <!-- Tracks List -->
            <div class="rounded-md bg-card-bg-default p-3">
              <div class="flex items-center justify-between mb-3">
                <p class="text-[10px] font-bold uppercase tracking-wider text-text-dimmed">Tracks in this release</p>
                @if (hasMultipleDiscs()) {
                  <span class="text-[10px] font-medium text-text-dimmed">
                    {{ albumDiscs().length }} discs • {{ album.songs?.length || 0 }} tracks
                  </span>
                }
              </div>
              
              @if (albumDiscs().length > 0) {
                <div class="space-y-3.5">
                  @for (disc of albumDiscs(); track disc.discNumber) {
                    <div class="space-y-1">
                      <!-- Disc Header (only shown if multi-disc) -->
                      @if (hasMultipleDiscs()) {
                        <div class="flex items-center gap-2 px-2 py-1 rounded-md bg-card-bg-hover/60 border border-card-border-border/40 text-xs">
                          <svg class="w-3.5 h-3.5 text-text-dimmed shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                          <span class="font-bold text-text-primary text-[11px] tracking-tight">
                            Disc {{ disc.discNumber }}
                          </span>
                          <span class="text-[10px] text-text-dimmed">
                            • {{ disc.tracks.length }} track{{ disc.tracks.length > 1 ? 's' : '' }}
                          </span>
                        </div>
                      }

                      <!-- Disc Tracks -->
                      <div class="space-y-0.5">
                        @for (song of disc.tracks; track song.id; let idx = $index) {
                          <div
                            (click)="onTrackClick(song.id)"
                            class="group flex items-center justify-between rounded-md p-2 transition-all hover:bg-card-bg-hover cursor-pointer select-none"
                          >
                            <div class="flex items-center gap-2.5 min-w-0">
                              <span class="w-4 text-center text-xs font-semibold text-text-dimmed group-hover:text-text-primary tabular-nums">
                                {{ song.trackNumber || (idx + 1) }}
                              </span>
                              <div class="min-w-0">
                                <p class="text-sm font-semibold text-text-primary truncate group-hover:text-primary transition-colors">
                                  {{ song.title }}
                                </p>
                                @if (song.explicit) {
                                  <span class="inline-block mt-0.5 rounded bg-card-bg-hover px-1 py-0.5 text-[8px] font-bold tracking-wide text-text-dimmed uppercase">
                                    Explicit
                                  </span>
                                }
                              </div>
                            </div>
                            
                            <div class="flex items-center gap-2.5 shrink-0 text-xs">
                              <span class="font-bold text-text-half-dimmed tabular-nums">
                                {{ formatStreams(song.streams) }}
                              </span>
                              <span class="text-text-dimmed min-w-7 text-right font-medium">
                                {{ formatDuration(song.durationMs) }}
                              </span>
                              <span class="text-text-dimmed group-hover:text-primary transition-colors text-base leading-none">
                                ›
                              </span>
                            </div>
                          </div>
                        }
                      </div>
                    </div>
                  }
                </div>
              } @else {
                <p class="text-xs text-center text-text-dimmed py-4">No tracks found for this release</p>
              }
            </div>

            @if (album.label) {
              <!-- Label / Copyright info -->
              <div class="rounded-md bg-card-bg-default p-3">
                <p class="text-[9px] font-bold uppercase tracking-wider text-text-dimmed mb-1">Record Label</p>
                <p class="text-xs font-semibold text-text-half-dimmed">{{ album.label }}</p>
              </div>
            }
          } @else {
            <div class="flex h-60 items-center justify-center text-center text-sm text-text-dimmed">
              Album details could not be loaded.
            </div>
          }
        </div>
      </div>

      <!-- Footer Button: Play on Spotify (12px padding) -->
      @if (albumDetails()?.spotifyId) {
        <div class="p-3 pt-2 shrink-0 bg-side-drawer-bg">
          <app-button
            variant="primary"
            size="large"
            class="w-full block"
            className="w-full justify-center"
            label="Play on Spotify"
            (btnClick)="playOnSpotify()"
          />
        </div>
      }
    </div>
  }
</aside>
